import { useState } from "react";
import { Heart, Mail, Phone, MapPin, Instagram, Facebook, Linkedin, Send, Sparkles, Globe, Zap } from "lucide-react";
import { motion, type Variants } from "framer-motion";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";


const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
};

const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] as const },
    },
};

export const Footer = () => {
    const [formData, setFormData] = useState({ name: "", email: "", details: "" });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.details) {
            toast.error("Please fill in all fields");
            return;
        }
        toast.success("Message sent successfully!");
        setFormData({ name: "", email: "", details: "" });
    };

    const contactItems = [
        { icon: MapPin, value: "123 Civic Center Dr", color: "from-amber-400 to-orange-500" },
        { icon: Mail, value: "hello@civicircle.com", href: "mailto:hello@civicircle.com", color: "from-blue-400 to-cyan-500" },
        { icon: Phone, value: "(+61) 833-112333", href: "tel:+61833112333", color: "from-emerald-400 to-teal-500" },
    ];

    const socialItems = [
        { icon: Instagram, hoverBg: "hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500" },
        { icon: Facebook, hoverBg: "hover:bg-blue-600" },
        { icon: Linkedin, hoverBg: "hover:bg-blue-700" },
    ];

    return (
        <footer className="relative overflow-hidden" style={{ backgroundColor: "hsl(220, 25%, 10%)" }}>
            {/* Animated Background */}
            <div className="absolute inset-0 overflow-hidden">
                <motion.div
                    className="absolute -top-10 -left-10 w-40 h-40 rounded-full blur-3xl"
                    style={{ background: "linear-gradient(to bottom right, hsl(188, 80%, 45%, 0.25), hsl(217, 90%, 60%, 0.2))" }}
                    animate={{ y: [-10, 10, -10], x: [-5, 5, -5], scale: [1, 1.1, 1] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="absolute top-1/2 right-0 w-48 h-48 rounded-full blur-3xl"
                    style={{ background: "linear-gradient(to bottom left, hsl(265, 85%, 60%, 0.2), hsl(280, 85%, 55%, 0.15))" }}
                    animate={{ y: [10, -15, 10], scale: [1, 1.15, 1] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="absolute -bottom-10 left-1/3 w-36 h-36 rounded-full blur-3xl"
                    style={{ background: "linear-gradient(to top right, hsl(160, 85%, 45%, 0.2), hsl(168, 80%, 50%, 0.15))" }}
                    animate={{ y: [-8, 12, -8], x: [5, -5, 5] }}
                    transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
                />
                {[...Array(5)].map((_, i) => (
                    <motion.div
                        key={i}
                        className="absolute w-1.5 h-1.5 rounded-full"
                        style={{
                            left: `${20 + i * 18}%`,
                            top: `${25 + (i % 3) * 20}%`,
                            background: `linear-gradient(135deg, ${i % 3 === 0
                                ? "hsl(188, 80%, 45%), hsl(217, 90%, 60%)"
                                : i % 3 === 1
                                    ? "hsl(270, 60%, 65%), hsl(330, 80%, 65%)"
                                    : "hsl(160, 85%, 45%), hsl(168, 80%, 50%)"
                                })`,
                        }}
                        animate={{ y: [-15, 15, -15], opacity: [0.4, 0.8, 0.4], scale: [1, 1.3, 1] }}
                        transition={{ duration: 4 + i, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                    />
                ))}
            </div>

            <motion.div
                className="max-w-7xl mx-auto px-4 sm:px-6 py-8 relative z-10"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={containerVariants}
            >
                <div className="grid grid-cols-1 lg:grid-cols-[1fr,1.1fr] gap-6 lg:gap-8">
                    {/* Left - Info & Contact */}
                    <motion.div variants={itemVariants} className="space-y-4">
                        <div className="flex items-start gap-3">
                            <motion.div
                                className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/25"
                                animate={{ rotate: [0, 5, -5, 0] }}
                                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            >
                                <Globe className="h-5 w-5 text-white" />
                            </motion.div>
                            <div>
                                <h2 className="font-display text-xl font-bold leading-tight" style={{ color: "hsl(0, 0%, 100%)" }}>
                                    Let's Build Something
                                    <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-violet-400 bg-clip-text text-transparent"> Amazing</span>
                                </h2>
                                <p className="text-sm mt-0.5" style={{ color: "hsl(217, 20%, 60%)" }}>
                                    Connecting civic professionals and supporters.
                                </p>
                            </div>
                        </div>

                        <div className="space-y-2">
                            {contactItems.map((item, index) => (
                                <motion.a
                                    key={item.value}
                                    href={item.href || "#"}
                                    className="group flex items-center gap-3 p-2.5 rounded-lg transition-all duration-300"
                                    style={{ backgroundColor: "hsla(0, 0%, 100%, 0.05)", border: "1px solid hsla(0, 0%, 100%, 0.1)" }}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.1 * index }}
                                    whileHover={{ x: 4 }}
                                >
                                    <motion.div
                                        className={`w-8 h-8 rounded-lg bg-gradient-to-br ${item.color} flex items-center justify-center shadow-md`}
                                        whileHover={{ scale: 1.1, rotate: 5 }}
                                    >
                                        <item.icon className="h-4 w-4" style={{ color: "hsl(0, 0%, 100%)" }} />
                                    </motion.div>
                                    <span className="text-sm transition-colors" style={{ color: "hsl(217, 15%, 75%)" }}>{item.value}</span>
                                </motion.a>
                            ))}
                        </div>

                        <div className="flex items-center gap-2 pt-1">
                            <motion.span
                                className="text-xs mr-1 flex items-center gap-1"
                                style={{ color: "hsl(217, 15%, 50%)" }}
                                animate={{ opacity: [0.5, 1, 0.5] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                <Sparkles className="h-3 w-3" style={{ color: "hsl(45, 100%, 55%)" }} />
                                Follow
                            </motion.span>
                            {socialItems.map((item, index) => (
                                <motion.a
                                    key={index}
                                    href="#"
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all duration-300 ${item.hoverBg}`}
                                    style={{ backgroundColor: "hsla(0, 0%, 100%, 0.05)", border: "1px solid hsla(0, 0%, 100%, 0.1)", color: "hsl(217, 15%, 65%)" }}
                                    whileHover={{ scale: 1.15, rotate: 5 }}
                                    whileTap={{ scale: 0.95 }}
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.4 + index * 0.1 }}
                                >
                                    <item.icon className="h-4 w-4" />
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>

                    {/* Right - Contact Form */}
                    <motion.div variants={itemVariants} className="relative">
                        <motion.div
                            className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500/30 via-blue-500/30 to-violet-500/30 rounded-xl blur-lg"
                            animate={{ opacity: [0.2, 0.4, 0.2] }}
                            transition={{ duration: 3, repeat: Infinity }}
                        />
                        <div className="relative bg-white rounded-xl p-4 shadow-xl border border-slate-200">
                            <div className="flex items-center gap-2 mb-3">
                                <motion.div
                                    className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md shadow-blue-500/25"
                                    animate={{ scale: [1, 1.05, 1] }}
                                    transition={{ duration: 2, repeat: Infinity }}
                                >
                                    <Mail className="h-4 w-4 text-white" />
                                </motion.div>
                                <div>
                                    <h3 className="font-semibold text-slate-900 text-sm">Send a Message</h3>
                                    <p className="text-[10px] text-slate-500">We'll respond within 24h</p>
                                </div>
                                <motion.div
                                    className="ml-auto"
                                    animate={{ rotate: 360 }}
                                    transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                                >
                                    <Zap className="h-4 w-4 text-amber-500" />
                                </motion.div>
                            </div>
                            <form onSubmit={handleSubmit} className="space-y-2">
                                <div className="grid grid-cols-2 gap-2">
                                    <Input
                                        placeholder="Your name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="bg-slate-50 border border-slate-300 focus:border-blue-500 rounded-lg h-9 text-sm"
                                    />
                                    <Input
                                        type="email"
                                        placeholder="your@email.com"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="bg-slate-50 border border-slate-300 focus:border-blue-500 rounded-lg h-9 text-sm"
                                    />
                                </div>
                                <Textarea
                                    placeholder="Your message..."
                                    value={formData.details}
                                    onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                                    className="bg-slate-50 border border-slate-300 focus:border-blue-500 rounded-lg min-h-[55px] resize-none text-sm"
                                />
                                <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
                                    <Button
                                        type="submit"
                                        className="w-full h-9 rounded-lg bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 hover:from-blue-700 hover:via-cyan-700 hover:to-teal-700 text-white font-medium text-sm shadow-lg"
                                    >
                                        <Send className="h-3.5 w-3.5 mr-1.5" />
                                        Send Message
                                        <motion.span className="ml-1.5" animate={{ x: [0, 3, 0] }} transition={{ duration: 1.5, repeat: Infinity }}>
                                            →
                                        </motion.span>
                                    </Button>
                                </motion.div>
                            </form>
                        </div>
                    </motion.div>
                </div>

                {/* Bottom Bar */}
                <motion.div
                    variants={itemVariants}
                    className="mt-6 pt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs"
                    style={{ borderTop: "1px solid hsla(0, 0%, 100%, 0.1)", color: "hsl(217, 15%, 50%)" }}
                >
                    <div className="flex items-center gap-4">
                        <span>© {new Date().getFullYear()} CiviCircle</span>
                        <motion.a href="#" style={{ color: "hsl(217, 15%, 50%)" }} whileHover={{ scale: 1.05 }}>
                            Privacy
                        </motion.a>
                        <motion.a href="#" style={{ color: "hsl(217, 15%, 50%)" }} whileHover={{ scale: 1.05 }}>
                            Terms
                        </motion.a>
                    </div>
                </motion.div>
            </motion.div>
        </footer>
    );
};
